import React, { useEffect, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Button,
  Stack,
  Card,
  Text,
  Flex,
  ScrollArea,
  CloseButton,
  Group,
  TagsInput,
  Tooltip,
  Select,
  Loader,
  MultiSelect,
  Textarea,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { getProductHardwareLabel } from "@utils/productUtils";
import { HardwareModels } from "types/productTypes";
import { IconUser, IconHash, IconCheck, IconX } from "@tabler/icons-react";
import {
  AccountType,
  AccountTypeForVersionManagement,
  accountTypeValues,
  Organization,
} from "../../types/accountManagementTypes";
import { checkUsernameAvailability } from "../../utils/api-communication/RestApiCli";
import { isValidOrgUsername } from "@utils/usernameUtils";
import { checkRole } from "@utils/CommonUtil";
import { Role } from "@model/role";
import { withDemoAndDistributorTypes } from "@constants/accountTypeOptions";

type UsernameStatus = "idle" | "checking" | "available" | "taken" | "error";

type RequestableAccountType = AccountType | AccountTypeForVersionManagement;

// Admins may request the version-management types, which Organization.accountType excludes.
export type OrgAccountRequestFormValues = Omit<Organization, "accountType"> & {
  accountType?: RequestableAccountType[];
};

const USERNAME_CHECK_DEBOUNCE_MS = 400;

const OTHER_ACCOUNT_TYPE: RequestableAccountType = "other";

export function showCreateOrgRequestError(error: unknown, t: (key: string) => string) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  notifications.show({
    color: "red",
    message: status === 409 ? t("usernameTaken") : t("requestFailed"),
  });
}

interface OrganizationAccountRequestFormProps {
  onSubmit: (data: OrgAccountRequestFormValues) => void;
  // optional initial values for reuse
  initialData?: OrgAccountRequestFormValues;
  // options to show for name select (value/label)
  orgOptions?: { value: string; label: string }[];
  // called when user types into the name search area
  onSearchName?: (val: string) => void;
  // called on any value change
  onChange?: (data: OrgAccountRequestFormValues) => void;
  // when true, hide the submit / add product actions (useful for embedding inline)
  hideSubmit?: boolean;
}

export function OrganizationAccountRequestForm({
  onSubmit,
  initialData,
  orgOptions,
  onSearchName,
  onChange,
  hideSubmit,
}: OrganizationAccountRequestFormProps) {
  const { t } = useTranslation();
  const hardwareModelOptions = Object.values(HardwareModels).map((model) => ({
    value: model,
    label: getProductHardwareLabel(model),
  }));
  const selectableAccountTypes: RequestableAccountType[] = withDemoAndDistributorTypes(
    accountTypeValues,
    checkRole([Role.Admin]),
  );
  const accountTypeOptions = selectableAccountTypes.map((type) => ({
    value: type,
    label: type.toUpperCase(),
  }));
  const form = useForm<OrgAccountRequestFormValues>({
    initialValues: initialData || {
      name: "",
      orgId: "",
      address: "",
      email: "",
      products: [{ hardwareModel: "", serialNo: "", alias: "" }],
      emailsToInform: [],
      accountType: [],
      accountInfo: "",
    },

    validate: {
      orgId: (value) => {
        if (!value?.trim()) {
          return null;
        }
        return isValidOrgUsername(value) ? null : t("usernameInvalid");
      },
      email: (value) => (/^\S+@\S+$/.test(value) ? null : t("invalidEmail")),
      accountType: (value) => (value?.length ? null : t("accountTypeEmpty")),
      accountInfo: (value, values) =>
        values.accountType?.includes(OTHER_ACCOUNT_TYPE) && !value?.trim()
          ? t("accountInfoRequiredForOther")
          : null,
      emailsToInform: (value) => {
        if (value.length === 0) {
          return t("emailsToInformEmpty");
        }
        for (const email of value) {
          if (!/^\S+@\S+$/.test(email)) {
            return t("invalidEmail");
          }
        }
        return null;
      },
    },
  });

  const [opened, setOpened] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const latestUsernameRef = useRef(form.values.orgId ?? "");

  const [debouncedUsername] = useDebouncedValue(form.values.orgId, USERNAME_CHECK_DEBOUNCE_MS);
  const isAccountInfoRequired = form.values.accountType?.includes(OTHER_ACCOUNT_TYPE) ?? false;

  useEffect(() => {
    const candidate = debouncedUsername?.trim() ?? "";
    if (!isValidOrgUsername(candidate)) {
      setUsernameStatus("idle");
      return;
    }
    setUsernameStatus("checking");
    const controller = new AbortController();
    const isStale = () => candidate !== (latestUsernameRef.current ?? "").trim();
    checkUsernameAvailability(
      candidate,
      (response) => {
        if (isStale()) return;
        setUsernameStatus(response.data?.available ? "available" : "taken");
      },
      () => {
        if (isStale()) return;
        setUsernameStatus("error");
      },
      controller.signal,
    );
    return () => controller.abort();
  }, [debouncedUsername]);

  // whenever the form values change, notify parent if onChange provided
  const notifyChange = () => {
    if (typeof onChange === "function") {
      onChange(form.values);
    }
  };

  const handleSubmit = () => {
    if (usernameStatus === "taken") {
      form.setFieldError("orgId", t("usernameTaken"));
      return;
    }
    if (usernameStatus === "checking") {
      form.setFieldError("orgId", t("usernameStillChecking"));
      return;
    }
    onSubmit({
      ...form.values,
      orgId: form.values.orgId?.trim() || undefined,
      accountInfo: form.values.accountInfo?.trim() || undefined,
    });
  };

  const usernameRightSection =
    usernameStatus === "checking" ? (
      <Loader size={16} />
    ) : usernameStatus === "available" ? (
      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
    ) : usernameStatus === "taken" ? (
      <IconX size={16} color="var(--mantine-color-red-6)" />
    ) : null;

  return (
    <form
      onSubmit={form.onSubmit(() => {
        handleSubmit();
      })}
    >
      {/* Name: TextInput with a dropdown list rendered in a ScrollArea from orgOptions */}
      {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
      {/* Note: keep searchValue separate so user can type without immediately overwriting form value */}
      <TextInput
        label={t("organizationName")}
        placeholder={t("organizationNamePlaceholder")}
        value={form.values.name || ""}
        onChange={(event) => {
          const val = event.currentTarget.value;
          form.setFieldValue("name", val);
          if (typeof onSearchName === "function") onSearchName(val);
          // open dropdown when user types
          setOpened(true);
          notifyChange();
        }}
        rightSection={
          form.values.name ? (
            <CloseButton
              onClick={() => {
                form.setFieldValue("name", "");
                if (typeof onSearchName === "function") onSearchName("");
                setOpened(false);
                notifyChange();
              }}
            />
          ) : null
        }
        onFocusCapture={() => {
          setOpened(true);
        }}
      />

      {opened && orgOptions && orgOptions.length > 0 && (
        <ScrollArea h={200} mt="sm">
          <Button.Group orientation="vertical" w="100%">
            {orgOptions.map((item, index) => (
              <Button
                key={index}
                onClick={() => {
                  // set the selected name (use label for display but keep value if needed)
                  form.setFieldValue("name", item.value || item.label || "");
                  notifyChange();
                  setOpened(false);
                }}
                variant="default"
                display={"flex"}
                style={{ justifyContent: "space-between" }}
                w={"100%"}
              >
                <Flex w={"100%"} justify={"space-between"}>
                  <Group wrap="nowrap">
                    <IconUser size="1rem" />
                    <Text ta="left">{item.label}</Text>
                  </Group>

                  <Group wrap="nowrap" justify="flex-end">
                    <IconHash size="1rem" />
                    <Tooltip label={"Value: " + (item.value || "")} position="left" withArrow>
                      <Text w={"70%"} ta="right" truncate>
                        {item.value}
                      </Text>
                    </Tooltip>
                  </Group>
                </Flex>
              </Button>
            ))}
          </Button.Group>
        </ScrollArea>
      )}

      <TextInput
        label={t("username")}
        placeholder={t("usernamePlaceholder")}
        value={form.values.orgId || ""}
        onChange={(event) => {
          latestUsernameRef.current = event.currentTarget.value;
          form.setFieldValue("orgId", event.currentTarget.value);
          form.clearFieldError("orgId");
          setUsernameStatus("idle");
          notifyChange();
        }}
        rightSection={usernameRightSection}
        error={form.errors.orgId ?? (usernameStatus === "taken" ? t("usernameTaken") : undefined)}
      />
      {usernameStatus === "available" && (
        <Text size="xs" c="teal.7">
          {t("usernameAvailable")}
        </Text>
      )}
      {usernameStatus === "error" && (
        <Text size="xs" c="orange.7">
          {t("usernameCheckFailed")}
        </Text>
      )}

      <TextInput
        label={t("address")}
        placeholder={t("addressPlaceholder")}
        value={form.values.address}
        onChange={(event) => {
          form.setFieldValue("address", event.currentTarget.value);
          notifyChange();
        }}
        error={form.errors.address}
      />

      <TextInput
        label={t("email")}
        placeholder={t("emailPlaceholder")}
        value={form.values.email}
        onChange={(event) => {
          form.setFieldValue("email", event.currentTarget.value);
          notifyChange();
        }}
        error={form.errors.email}
      />
      <Tooltip label={t("emailsToInformTooltip")} withArrow position="bottom" multiline w={220}>
        <TagsInput
          label={t("emailsToInform")}
          data={form.values.emailsToInform}
          placeholder={t("emailsToInformPlaceholder")}
          clearable
          {...form.getInputProps("emailsToInform")}
        />
      </Tooltip>

      <MultiSelect
        withAsterisk
        label={t("accountType")}
        placeholder={t("accountTypePlaceholder")}
        data={accountTypeOptions}
        value={form.values.accountType || []}
        searchable
        clearable
        onChange={(value) => {
          form.setFieldValue(
            "accountType",
            selectableAccountTypes.filter((type) => value.includes(type)),
          );
          form.clearFieldError("accountInfo");
          notifyChange();
        }}
        error={form.errors.accountType}
      />

      <Textarea
        withAsterisk={isAccountInfoRequired}
        label={t("accountInfo")}
        placeholder={t("accountInfoPlaceholder")}
        value={form.values.accountInfo || ""}
        onChange={(event) => {
          form.setFieldValue("accountInfo", event.currentTarget.value);
          notifyChange();
        }}
        error={form.errors.accountInfo}
      />

      {form.values.products.map((product, index) => (
        <Card mt={"sm"} p={"sm"} style={{ background: "var(--oculera-surface-alt)" }} key={index}>
          <Flex justify={"space-between"} align={"center"}>
            <Text>{t("product", { number: index + 1 })}</Text>
            {!hideSubmit && (
              <Button
                onClick={() => {
                  const newProducts = [...form.values.products];
                  newProducts.splice(index, 1);
                  form.setFieldValue("products", newProducts);
                  notifyChange();
                }}
              >
                {t("removeProduct")}
              </Button>
            )}
          </Flex>
          <Select
            label={t("hardwareModel")}
            placeholder={t("hardwareModelPlaceholder")}
            data={hardwareModelOptions}
            value={product.hardwareModel}
            onChange={(value) => {
              form.setFieldValue(`products.${index}.hardwareModel`, value || "");
              notifyChange();
            }}
          />
          <TextInput
            label={t("serialNo")}
            placeholder={t("serialNoPlaceholder")}
            value={product.serialNo}
            onChange={(event) => {
              form.setFieldValue(`products.${index}.serialNo`, event.currentTarget.value);
              notifyChange();
            }}
            error={form.errors.products?.[index]?.serialNo?.message}
          />

          <TextInput
            label={t("alias")}
            placeholder={t("aliasPlaceholder")}
            value={product.alias}
            onChange={(event) => {
              form.setFieldValue(`products.${index}.alias`, event.currentTarget.value);
              notifyChange();
            }}
            error={form.errors.products?.[index]?.alias?.message}
          />
        </Card>
      ))}

      <Stack w={"30%"}>
        <Button
          mt={"sm"}
          onClick={() => {
            form.setFieldValue("products", [
              ...form.values.products,
              { hardwareModel: "", serialNo: "", alias: "" },
            ]);
            notifyChange();
          }}
        >
          {t("addProduct")}
        </Button>
        {!hideSubmit && <Button type="submit">{t("submit")}</Button>}
      </Stack>
    </form>
  );
}
